import { createApp } from 'vue'
import { createPinia } from 'pinia'
import VueApexCharts from 'vue3-apexcharts'

import App from './App.vue'
import { router } from './router'
import { installPrimeVue } from './plugins/primevue'
import { setUnauthorizedHandler } from './api/client'
import { useAuthStore } from './stores/auth'

import './assets/styles/main.css'

/**
 * Red de seguridad para la pestaña que ya estaba abierta cuando se desplegó.
 *
 * Los archivos del bundle llevan hash en el nombre, así que un despliegue los
 * reemplaza por otros. Una pestaña con el `index.html` anterior sigue pidiendo
 * los de antes y, al navegar, el import en diferido falla: pantalla en blanco
 * sin nada que explique por qué. Vite avisa de eso con `vite:preloadError`, y
 * la única salida es recargar para tomar el index nuevo.
 *
 * La marca en sessionStorage es lo que corta el bucle: se guarda CUÁNDO se
 * recargó, y no se vuelve a intentar dentro de la misma ventana. Si tras
 * recargar sigue fallando, el problema no es el despliegue —y recargar en
 * redondo solo lo esconde—; pasada la ventana, un despliegue posterior sí
 * puede volver a intentarlo.
 */
const RELOAD_FLAG = 'vetalia.reloaded-for-stale-bundle'
const RELOAD_WINDOW_MS = 60_000

window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault()

  const last = Number(sessionStorage.getItem(RELOAD_FLAG)) || 0
  if (Date.now() - last < RELOAD_WINDOW_MS) return

  sessionStorage.setItem(RELOAD_FLAG, String(Date.now()))
  window.location.reload()
})

const app = createApp(App)

app.use(createPinia())
app.use(router)
installPrimeVue(app)
app.use(VueApexCharts)

// Un 401 en cualquier llamada significa que la cookie caducó del lado del
// servidor. Se limpia la sesión local y el guard del router hace el resto:
// sin esto, la pantalla se quedaría vacía sin decir por qué.
setUnauthorizedHandler(() => {
  useAuthStore().clear()
  if (router.currentRoute.value.name !== 'login') {
    router.push({ name: 'login', query: { redirect: router.currentRoute.value.fullPath } })
  }
})

app.mount('#app')

// El router decide la primera pantalla de forma asíncrona (espera la sonda de
// sesión), así que el splash se retira cuando esa decisión ya está tomada —no
// al montar—. Al revés se vería un parpadeo en blanco entre los dos.
router.isReady().finally(() => {
  const splash = document.getElementById('app-splash')
  if (!splash) return
  splash.classList.add('hidden')
  setTimeout(() => splash.remove(), 220)
})
